/* Angular + external */
import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  AfterViewInit,
  QueryList,
  Renderer2,
  Inject,
  ChangeDetectionStrategy,
} from "@angular/core";
import { ViewChildren, ElementRef, ViewChild } from "@angular/core";
import { Input, Output, EventEmitter, SimpleChanges, ChangeDetectorRef } from "@angular/core";
import { Subscription } from "rxjs";

/***********************/
/* BFX */
// multi-asset
import * as CubiqTS from "shared/cubiq-model-multi-asset";
// cubiq-services
import {
  MarketDataEventTransformer,
  RateSubscriptionPromiseService,
  TraderGroupPromiseLookupService,
  MarkupCriteria,
  RateCache,
  CcyPairPromiseLookupService,
  RatePromiseService,
  CcyPairLookupService,
  TraderGroupLookupService,
  CurrencyPairCalendarPromiseLookupService,
  SystemConfigPromiseService,
  SysFeatures,
  BranchLookupService,
  ClientLookupService,
} from "shared/cubiq-services";

// global-services

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TranslateService } from "@ngx-translate/core";
// order-entry services
import { OESettingsService } from "../services/basic/oe-settings.service";
import { OECoordinatorService } from "../services/oe-coordinator.service";
import { OEApiService } from "../services/oe-api.service";
import {
  OEUtilityService,
  PRIVATIZATION_LABEL,
  RESTRICTED_LABEL,
} from "../services/basic/oe-utility.service";
// order-entry  models
import { OEPanelAlgoModel } from "../data/models/oe-panel-algo.model";
import {
  OEInputLockStateItemModel,
  OELockingProfileTransportModel,
} from "../data/models/oe-locking-profile.model";
import { OECommandEventModel } from "../data/models/oe-command-event.model";
import { OEInputModel } from "../data/models/oe-input.model";
//  order-entry  enums
import { OENInputControl } from "../data/enums/oe-input-control.enum";
import { OENPanel } from "../data/enums/oe-panel.enum";
import { OENLockedState } from "../data/enums/oe-locked-state.enum";
import { OENCommand } from "../data/enums/oe-command.enum";
// interface controls components
import { ICAutocompleteComponent } from "./controls/ic-autocomplete.component";
import { BfxContactsInputComponent } from "./controls/bfx-contacts-input.component";

import { BfxClientIntentToggleComponent } from "./controls/bfx-toggle-client-intent.component";
import { BfxAmountSpinnerComponent } from "./controls/bfx-amount-spinner.component";

import { BfxValueDateComponent } from "../interface/controls/bfx-value-date.component";
import { BfxMarkupComponent } from "../interface/controls/bfx-markup.component";
import { BfxGoodFromUntilComponent } from "./controls/good-from-until/bfx-good-from-until.component";
import { BfxAutoCompleteComponent } from "app/bfx-element-library/bfx-autocomplete.component";
import { BfxSuspendControlComponent } from "../interface/controls/bfx-suspend-control.component";
// interface controls enums
import { ICNInputControlState } from "./controls/models/bfx-interface-controls.enums";
// interface controls models
import {
  ICFixedCcyModel,
  ICAutocompleteGenericModel,
  ICMarkupModel,
  ICTenorValueDateModel,
} from "./controls/models/bfx-interface-controls.models";

import { ICHoldSTPComponent } from "./controls/ic-hold-stp.component";
import { ICPrivateOrderComponent } from "./controls/ic-private-order.component";
import { OEDLoadingDataModel } from "../data/models/oe-loading-data.model";
import { OENEntryType } from "../data/enums/oe-entry-type.enum";
import { ICAlgoTypeComponent } from "./controls/ic-algo-type.component";
import { ICLimitPriceModel } from "./controls/models/ic-limit-price.model";
import {
  INPUT_TIME_SHOWSECONDS,
  INPUT_TIME_PATTERN,
  SECS_VALIDATE_TIME_PATTERN,
  SECS_MASK,
  INPUT_TIME_MASK,
  INPUT_TIME_DEFAULT,
  SECS_DEFAULT,
} from "./controls/bfx-time-input.component";
import { OERateDisplayModel } from "../data/models/rate/oe-rate-display.model";
import { OERateTransportModel } from "../data/models/rate/oe-rate-transport.model";
import { OERateDataModel } from "../data/models/rate/oe-rate-data.model";
import { OELoggerService } from "../services/basic/oe-logger.service";
import { OENOperationMode } from "../data/enums/oe-operation-mode.enum";
import { OEAlgoParamsComponent } from "./oe-panel-algo-params.component";
import { OENLockingProfileType } from "../data/enums/oe-locking-profile-type.enum";
import { ICCcyPairStringComponent } from "./controls/ic-ccy-pair-string.component";
import { ICFixedCcyComponent } from "../../ic-controls/ic-fixed-ccy.component";
import { InputText } from "@bfx/primeng/inputtext";
import { BfxConfigService } from "shared/auth-services/system-config/bfx-config.service";
import { OEDatasetService } from "app/order-entry/services/basic/oe-dataset.service";
import { ICCustomFieldsComponent } from "app/order-entry/interface/controls/ic-custom-fields.component";
import { ICAutoFwdPricingComponent } from "./controls/ic-auto-fwd-pricing.component";
import { OEModelGetterService } from "../services/model/oe-model-getter.service";
import { OEAdapterForwardService } from "../services/adapter/oe-adapter-forward.service";

const BIG_NUMBER_ZERO: CubiqTS.BigNumber = new CubiqTS.BigNumber(0);

@Component({
  selector: "oe-panel-algo-entry",
  templateUrl: "./html/oe-panel-algo-entry.html",
  providers: [
    { provide: INPUT_TIME_SHOWSECONDS, useValue: true },
    { provide: INPUT_TIME_PATTERN, useValue: SECS_VALIDATE_TIME_PATTERN },
    { provide: INPUT_TIME_MASK, useValue: SECS_MASK },
    { provide: INPUT_TIME_DEFAULT, useValue: SECS_DEFAULT },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OEAlgoEntryComponent implements OnDestroy, OnInit, OnChanges, AfterViewInit {
  @Input() refId: string;
  @Input() rootRefId: string; // refId of the root order
  @Input() entryType: OENEntryType;
  @Input() operationMode: OENOperationMode;

  /* Tab Index binding */
  tabIndex: number;

  @Output() dataLoadCompleted: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output() tabIndexChange: EventEmitter<number> = new EventEmitter<number>();

  @Input()
  get tab() {
    return this.tabIndex;
  }

  set tab(value: any) {
    this.tabIndex = value;
    this.tabIndexChange.emit(this.tabIndex);
  }

  /**********************************************************************/
  /* DATA */
  public control: {
    showIntentAndAmount: boolean;
    multiplyAmountUserValue: number;
    multiplyAmountUserToggle: boolean;
    commentSize: number;
    lockAllFg: boolean;
    isLoading: boolean;
    initialLoadingComplete: boolean;
    viewRenderComplete: boolean;
    loadInterval: {
      interval: any;
      loopCounter: number;
      loopLimit: number;
      loopTimeout: number;
    };
    lockingProfileData: Array<OEInputLockStateItemModel> | null;
    clientAccountConstraintAutoSelect: boolean;
    traderGroupAutocompleteConfig: {
      allowEmpty: boolean;
      forceSelection: boolean;
      customValidation: any;
    };
  } = {
    showIntentAndAmount: true,
    multiplyAmountUserValue: 0,
    multiplyAmountUserToggle: false,
    commentSize: 250,
    lockAllFg: false,
    isLoading: false,
    initialLoadingComplete: false,
    viewRenderComplete: false,
    loadInterval: {
      interval: null,
      loopCounter: 0,
      loopLimit: 100,
      loopTimeout: 100,
    },
    lockingProfileData: null,
    clientAccountConstraintAutoSelect: true,
    traderGroupAutocompleteConfig: {
      allowEmpty: true,
      forceSelection: true,
      customValidation: null,
    },
  };

  public tabData: any = {
    financials: {
      label: "Financials",
      label_key: "order_entry.screen.tabs.label.financials",
    },
    more: {
      label: "More",
      label_key: "order_entry.screen.tabs.label.more",
    },
  };

  public rate = {
    value: null,
    empty: true,
  };
  /*
	private previousValue = {
		client: null,  // ClientImpl
		clientId: null, // string
	}; */

  public data: OEPanelAlgoModel = new OEPanelAlgoModel();

  public markupCheckboxEnabled: boolean = false;
  private fwdEnabled: boolean = false;
  public latestRateData: OERateTransportModel = null;

  // subscriptions
  private coordinatorSubscription: Subscription;
  private rateSubscription: Subscription;

  public oboEnabled: boolean = true;

  public hasCustomFields: boolean = true;

  public isNDFAlgo: boolean = false;

  /**********************************************************************/
  @ViewChild("tabs", { static: true }) elTabs: any;
  /* CLIENT -  Interface References */
  @ViewChildren("inputClient") icClient: QueryList<ICAutocompleteComponent>;
  @ViewChildren("inputAccount") icAccount: QueryList<ICAutocompleteComponent>;
  @ViewChildren("inputClientTrader")
  icClientTrader: QueryList<ICAutocompleteComponent>;
  @ViewChildren("inputContacts")
  contactsInputControl: QueryList<BfxContactsInputComponent>;
  /* FINANCIALS -  Interface References */
  @ViewChildren("inputCcyPair") icCcyPair: QueryList<ICCcyPairStringComponent>;
  @ViewChildren("inputAlgoType") icAlgoType: QueryList<ICAlgoTypeComponent>;
  @ViewChildren("toggleClientIntent")
  clientIntentToggleControl: QueryList<BfxClientIntentToggleComponent>;
  @ViewChildren("toggleFixedCcy")
  fixedCcyControl: QueryList<ICFixedCcyComponent>;
  @ViewChildren("inputOrderAmount")
  orderAmountInputControl: QueryList<BfxAmountSpinnerComponent>;
  @ViewChildren("inputValueDate")
  valueDateInputControl: QueryList<BfxValueDateComponent>;
  @ViewChildren("inputMarkup")
  markupInputControl: QueryList<BfxMarkupComponent>;
  @ViewChildren("inputGoodFrom")
  goodFromInputControl: QueryList<BfxGoodFromUntilComponent>;
  @ViewChildren("inputGoodUntil")
  goodUntilInputControl: QueryList<BfxGoodFromUntilComponent>;
  @ViewChildren("inputTraderGroup")
  traderGroupInputControl: QueryList<BfxAutoCompleteComponent>;
  @ViewChildren("inputSuspend")
  suspendInputControl: QueryList<BfxSuspendControlComponent>;
  /* MORE -  Interface References */
  @ViewChild("inputCommentClient") icCommentClient: ElementRef;

  @ViewChildren("inputHoldSTP") icHoldSTP: QueryList<ICHoldSTPComponent>;
  @ViewChildren("autoFwdPricing") icAutoFwdPricing: QueryList<ICAutoFwdPricingComponent>;
  @ViewChildren("inputPrivateOrder")
  icPrivateOrder: QueryList<ICPrivateOrderComponent>;

  /* ALGO PARAMS - Panel References */
  @ViewChild("panelAlgoParams") panelAlgoParams: OEAlgoParamsComponent;

  /* OBO */
  @ViewChildren("oboClient") oboClient: QueryList<InputText>;
  @ViewChildren("oboAccount") oboAccount: QueryList<InputText>;
  @ViewChildren("customFieldComponent") customFields: QueryList<ICCustomFieldsComponent>;

  /**********************************************************************/
  /* CONSTRUCTOR  */
  constructor(
    protected translate: TranslateService,
    protected settings: OESettingsService,
    protected coordinator: OECoordinatorService,
    protected traderGroupService: TraderGroupPromiseLookupService,
    private getter: OEModelGetterService,
    private forwardAdapter: OEAdapterForwardService,
    protected utility: OEUtilityService,
    protected api: OEApiService,
    protected cd: ChangeDetectorRef,
    //
    protected rateService: RatePromiseService,
    protected rateCache: RateCache,
    protected rateTransformer: MarketDataEventTransformer,
    protected rateSubscriptionService: RateSubscriptionPromiseService,
    protected currencyPairService: CcyPairPromiseLookupService,
    protected calendarService: CurrencyPairCalendarPromiseLookupService,
    protected oeLogger: OELoggerService,
    public renderer2: Renderer2,
    protected sysConfigService: SystemConfigPromiseService,
    protected configService: BfxConfigService,
    protected branchLookupService: BranchLookupService,
    protected clientLookupService: ClientLookupService,
    datasetService: OEDatasetService,
    @Inject(INPUT_TIME_SHOWSECONDS) public in_showSeconds: boolean,
    @Inject(INPUT_TIME_MASK) public in_mask: string,
    @Inject(INPUT_TIME_PATTERN) public in_pattern: RegExp,
    @Inject(INPUT_TIME_DEFAULT) public in_default: string,
  ) {
    this.oeLogger = oeLogger.for(__dirname);
    this.sysConfigService.getSystemConfig(SysFeatures.fwdEnabled).then((value) => {
      this.fwdEnabled = "Y" == value || "y" == value ? true : false;
    });
    configService.getUIConfig().then((config) => {
      this.oboEnabled = config && config.general ? config.general.oboEnabled : false;
    });

    this.hasCustomFields = datasetService?.customFieldDefns?.editable?.length > 0;
  }

  /**********************************************************************/
  /* LIFE CYCLE HOOKS */

  ngOnInit() {
    this.initialize();
  }

  ngOnDestroy() {
    this.unsubscribeRate(this.rootRefId, this.data.currencyPair.product);
    this.coordinatorSubscription.unsubscribe();
    if (this.rateSubscription) {
      this.rateSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    for (let propName in changes) {
      switch (propName) {
        case "rootRefId":
          if (this.data.previousValue.rootRefId == null) {
            this.data.previousValue.rootRefId = this.rootRefId;
          } else if (
            typeof this.data.previousValue.rootRefId === "string" &&
            this.data.previousValue.rootRefId !== this.rootRefId
          ) {
            // If there is a root change (copy or edit), subscribe new rootRefId and unsubscribe old rootRefId
            this.api.takeRateSubscription(
              this.rootRefId,
              this.data.previousValue.rootRefId,
              this.entryType,
              this.data.currencyPair.product,
            );
            this.data.previousValue.rootRefId = this.rootRefId;
          }
          break;
        case "entryType":
          // move along, nothing to see here!
          break;
      }
    }
  }

  ngAfterViewInit() {
    this.control.viewRenderComplete = true;
    this.makeTabsUntabbable();
    if (this.operationMode === OENOperationMode.NEW) {
      setTimeout(() => {
        this.propagateMarkupDefaultData();
      }, 200);
    }
  }

  /**********************************************************************/
  /* INITIALIZATION */

  protected initialize(): void {
    this.initializeSettings();
    this.initializeLabelTranslations();
    // create dataset
    this.initializeAlgoTypeDataset();
    this.currencyPairsGetDataset();
    this.initializeTraderGroups();
    // subscriptions
    this.subscribeToCoordinator();
    //this.subscribeToRateEvents();
  }

  private initializeSettings(): void {
    // client panel
    let clientSettings = this.settings.getSysConfigForClientPanel();
    this.data.clientTrader.enabled = clientSettings.clientTraderEnabled;
    this.data.contacts.size = clientSettings.contactsRenderSize;
    // financials panel
    let financialsSettings = this.settings.getSysConfigForFinancialsPanel();
    this.data.markup.config.markupCheckboxSelected = financialsSettings.markupAlgoCbx;
    this.data.markup.config.markupEnabled = financialsSettings.markupAlgoEnabled;
    this.data.markup.config.markupEntry = financialsSettings.markupAllowAlgoEntry;
    this.data.markup.config.markupType = financialsSettings.markupRetrievalTypeAlgo;
    this.data.markup.config.markupLimitDefault = financialsSettings.markupAlgoDefault;
    this.data.markup.config.markupRetrievalEnabled = financialsSettings.markupRetrievalEnabled;
    this.data.suspend.show = false; // this.data.suspend.showConfig = financialsSettings.showSuspend;
    this.data.traderGroup.traderGroupModifiable = financialsSettings.traderGroupModifiableAlgo;
    // more panel
    let moreSettings = this.settings.getSysConfigForMorePanel();
    this.data.autoFwdPricing.enabledConfig = moreSettings.autoForwardPricingEnabled;
    this.data.autoFwdPricing.manualOnly = moreSettings.manualForwardPricingOnlyALGO;
    this.data.privateOrder.show = this.data.privateOrder.showConfig = moreSettings.showPrivateOrder;
    this.data.commentPrivate.show = this.data.commentPrivate.showConfig =
      moreSettings.showCommentPrivate;
    this.data.holdSTP.enabledConfig = moreSettings.holdSTPisEnabled;
    this.data.commentClient.checkbox.show = moreSettings.showClientCommentCheckbox;
    // Profile settings
    this.control.multiplyAmountUserValue = this.settings.multiplyAmountUserValue;
    this.control.multiplyAmountUserToggle = this.settings.multiplyAmountUserToggle;
    this.initControlVisibility();
  }

  /**
   * define type-specific visibility - e.g. NDF algos do not have autoFwdpricing
   */
  protected initControlVisibility(): void {
    this.data.autoFwdPricing.show = true;
  }

  private initializeLabelTranslations(): void {
    this.tabData.financials.label = this.translate.instant(this.tabData.financials.label_key);
    this.tabData.more.label = this.translate.instant(this.tabData.more.label_key);
    /********* FINANCIALS PANEL *********/
    this.data.algoType.label = this.translate.instant(this.data.algoType.labelKey);
    this.data.currencyPair.label = this.translate.instant(this.data.currencyPair.labelKey);
    this.data.limitPrice.label = this.translate.instant(this.data.limitPrice.labelKey);
    this.data.orderAmount.label = this.translate.instant(this.data.orderAmount.labelKey);
    this.data.clientIntent.label = this.translate.instant(this.data.clientIntent.labelKey);
    this.data.valueDate.label = this.translate.instant(this.data.valueDate.labelKey);
    this.data.markup.label = this.translate.instant(this.data.markup.labelKey);
    this.data.goodFrom.label = this.translate.instant(this.data.goodFrom.labelKey);
    this.data.goodUntil.label = this.translate.instant(this.data.goodUntil.labelKey);
    this.data.suspend.to.label = this.translate.instant(this.data.suspend.to.labelKey);
    this.data.suspend.from.label = this.translate.instant(this.data.suspend.from.labelKey);
    /********* MORE PANEL *********/
    this.data.commentPrivate.label = this.translate.instant(this.data.commentPrivate.labelKey);
    this.data.commentClient.label = this.translate.instant(this.data.commentClient.labelKey);
    this.data.holdSTP.label = this.translate.instant(this.data.holdSTP.labelKey);
    //this.data.executionMode.label = this.translate.instant(this.data.executionMode.labelKey);
    this.data.privateOrder.label = this.translate.instant(this.data.privateOrder.labelKey);
    // this.data.partialFill.label = this.translate.instant(this.data.partialFill.labelKey);
  }

  protected initializeTraderGroups(): void {
    this.traderGroupService.isPrimed().then(() => {
      this.loadTraderGroups();
    });
  }

  private propagateMarkupDefaultData(): void {
    if (this.data.markup.config.markupEntry) {
      // markupLimitCbx - if enabled sets markup to 0 Pips
      if (!this.data.markup.value) {
        this.data.markup.value = new ICMarkupModel();
      }
      let markup = this.data.markup.value;
      let checkSelected = markup.checkbox.enabled == true && markup.checkbox.value == true;
      if (this.data.markup.config.markupCheckboxSelected == true || checkSelected) {
        // Set default values
        markup.value = BIG_NUMBER_ZERO;
        markup.textValue = BIG_NUMBER_ZERO.toString();
        markup.typeValue = new ICAutocompleteGenericModel(CubiqTS.MarkUpType.Pips, "Pips");
        markup.checkbox.enabled = true;
        markup.checkbox.value = this.data.markup.config.markupCheckboxSelected;
      }

      this.updateMarkup(markup);
      this.displayMarkupRetrievalButton();
      this.api.updateMarkup(this.refId, markup);
    }
  }

  /**********************************************************************/
  /* Tabs */

  public onTabActivate($event: any) {
    this.tab = $event.index;
  }

  private makeTabsUntabbable(): void {
    let financialsTab = this.elTabs.el.nativeElement.children[0].children[0].children[0];
    let moreTab = this.elTabs.el.nativeElement.children[0].children[0].children[1];
    this.renderer2.setAttribute(financialsTab, "tabIndex", "-1");
    this.renderer2.setAttribute(moreTab, "tabIndex", "-1");
  }

  /**********************************************************************/
  /* COORDINATOR HANDLER */
  private subscribeToCoordinator(): void {
    this.coordinatorSubscription = this.coordinator.entryPanelCmd$.subscribe(
      ($event: OECommandEventModel) => {
        if ($event.ref === this.refId) {
          this.cd.markForCheck();
          switch ($event.cmd) {
            case OENCommand.RESET_ALL:
              this.reset();
              break;
            case OENCommand.INVALID_INPUT:
              this.handleInvalidInput($event.data);
              break;
            case OENCommand.LOCK_ALL:
              this.lockAll();
              break;
            case OENCommand.UNLOCK_ALL:
              this.unlockAll();
              break;
            case OENCommand.LOAD_LOCKING_PROFILE:
              this.loadLockProfile($event.data);
              break;
            case OENCommand.LOAD_CONTROLS:
              this.load($event.data);
              if ($event.data instanceof OEDLoadingDataModel && !$event.data.singlePropagation) {
                let force =
                  this.operationMode === OENOperationMode.NEW &&
                  Array.isArray($event.data.values) &&
                  $event.data.values.find((item) => item.input === OENInputControl.ALGO_TYPE) ==
                    null;
                this.informLoadComplete(force);
              }
              break;
            case OENCommand.UNBULK:
            case OENCommand.BULKED:
            case OENCommand.BULKING: {
              break;
            }
            default:
              logger.out.error(
                `ERROR: ${
                  this.constructor.name
                }.subscribeToCoordinator(): Command not implemented! command= (${$event.cmd}) [${
                  $event.cmd != null ? OENCommand[$event.cmd] : "NULL"
                }]`,
              );
              break;
          }
        }
      },
    );
  }

  private informLoadComplete(force: boolean = false): void {
    this.control.loadInterval.interval = setInterval(() => {
      this.control.loadInterval.loopCounter++;
      if (this.panelAlgoParams.isReady() || force) {
        this.clearInformLoadInterval();
        this.dataLoadCompleted.emit(true);
      } else if (this.control.loadInterval.loopCounter > this.control.loadInterval.loopLimit) {
        this.clearInformLoadInterval();
        this.dataLoadCompleted.emit(true);
      }
    }, this.control.loadInterval.loopTimeout);
  }

  private clearInformLoadInterval(): void {
    this.control.loadInterval.loopCounter = 0;
    if (this.control.loadInterval.interval != null) {
      clearInterval(this.control.loadInterval.interval);
    }
  }
  /**********************************************************************/
  /* RESET */
  private reset(): void {
    this.resetData();
    this.resetLocking();
  }

  // handleResetAllControls
  protected resetData(): void {
    /********* CLIENT PANEL *********/
    this.icClient.forEach((c) => c.reset());
    this.icAccount.forEach((c) => c.reset());
    this.icClientTrader.forEach((c) => c.reset());
    this.contactsInputControl.forEach((c) => c.resetInputControl());
    /********* FINANCIALS PANEL *********/
    this.unsubscribeRate(this.rootRefId, this.data.previousValue.product);
    this.data.marketRate = new OERateDisplayModel();

    this.data.previousValue.client = null;
    this.data.previousValue.clientId = null;
    this.data.previousValue.inverted = false;
    this.data.previousValue.product = null;

    // Fixed ccy

    this.fixedCcyControl.forEach((c) => c.reset());
    this.data.fixedCcy.ccyPair = null;
    this.data.fixedCcy.fixedCcy = null;

    //
    this.data.currencyPair.inverted = false;
    this.control.showIntentAndAmount = true;
    //
    this.data.markup.criteria = null;
    // Limit Price
    this.data.limitPrice.loadRate = false;
    //
    this.data.currencyPair.product = null;
    this.icCcyPair.forEach((c) => c.reset());
    //
    this.icAlgoType.forEach((c) => c.reset());
    this.clientIntentToggleControl.forEach((c) => c.resetInputControl());
    this.orderAmountInputControl.forEach((c) => c.resetInputControl());

    // value date
    this.data.valueDate.dataset = null;
    this.valueDateInputControl.forEach((c) => c.resetInputControl());

    // Markup
    this.markupInputControl.forEach((c) => c.resetInputControl());
    setTimeout(() => {
      this.propagateMarkupDefaultData();
    }, 100);

    // suspend
    this.data.suspend.value = null;
    this.data.suspend.from.externalState = null;
    this.data.suspend.to.externalState = null;

    this.suspendInputControl.forEach((c) => c.resetInputs());

    // reset good from
    this.data.goodFrom.showSeconds = true;
    this.data.goodFrom.value = null;
    this.data.goodFrom.externalState = null;
    this.data.goodFrom.errorMsg = null;
    this.data.goodFrom.constraintValidFg = true;
    this.goodFromInputControl.forEach((c) => c.resetInputControl());
    // reset good until
    this.data.goodUntil.value = null;
    this.data.goodUntil.externalState = null;
    this.data.goodUntil.errorMsg = null;
    this.data.goodUntil.constraintValidFg = true;
    this.goodUntilInputControl.forEach((c) => c.resetInputControl());
    // traderGroup reset
    this.data.traderGroup.value = null;
    //this.data.traderGroup.externalState = null;
    this.data.traderGroup.traderGroupVisited = false;
    this.data.traderGroup.state = ICNInputControlState.INACTIVE;
    // traderGroup dataset reset
    this.data.traderGroup.traderGroups = [];
    this.data.traderGroup.allTraderGroups = [];
    this.data.traderGroup.limitTraderGroups = [];
    this.data.traderGroup.manualTraderGroups = [];
    this.data.traderGroup.benchmarkTraderGroups = [];
    this.initializeTraderGroups();
    /********* MORE PANEL *********/
    // reset private comment
    this.data.commentPrivate.value = null;
    this.data.commentPrivate.uiPrivatized = false;
    this.data.commentPrivate.externalState = null;
    // reset client comment
    this.data.commentClient.value = null;
    this.data.commentClient.uiPrivatized = false;
    this.data.commentClient.externalState = null;
    this.data.commentClient.checkbox.value = false;
    //  hold stp
    this.icHoldSTP.forEach((c) => c.reset());
    // autoFwdPricing
    this.icAutoFwdPricing.forEach((c) => c.reset());
    // reset private order
    this.data.privateOrder.show = this.data.privateOrder.showConfig;
    this.data.privateOrder.uiPrivatized = false;
    this.icPrivateOrder.forEach((c) => c.reset());

    // reset custom fields
    this.data.customValues.value = null;
    this.data.customValues.externalState = null;
    this.customFields.forEach((c) => c.resetInputControl());

    // OBOs
    this.data.oboClient.value = null;
    this.data.oboAccount.value = null;
  }

  protected resetLocking(): void {
    /********* CLIENT PANEL *********/
    this.data.client.lockState = OENLockedState.UNLOCKED;
    this.data.account.lockState = OENLockedState.UNLOCKED;
    this.data.contacts.lockState = OENLockedState.UNLOCKED;
    this.data.clientTrader.lockState = OENLockedState.UNLOCKED;
    /********* FINANCIALS PANEL *********/
    this.data.currencyPair.lockState = OENLockedState.UNLOCKED;
    this.data.algoType.lockState = OENLockedState.UNLOCKED;
    this.data.clientIntent.lockState = OENLockedState.UNLOCKED;
    this.data.fixedCcy.lockState = OENLockedState.UNLOCKED;
    this.data.orderAmount.lockState = OENLockedState.UNLOCKED;
    this.data.valueDate.lockState = OENLockedState.UNLOCKED;
    this.data.goodFrom.lockState = OENLockedState.UNLOCKED;
    this.data.goodUntil.lockState = OENLockedState.UNLOCKED;
    this.data.markup.lockState = OENLockedState.UNLOCKED;
    //this.data.tracking.lockState = OENLockedState.UNLOCKED;
    this.data.suspend.lockState = OENLockedState.UNLOCKED;
    this.data.traderGroup.lockState = OENLockedState.UNLOCKED;
    /********* MORE PANEL *********/
    this.data.commentPrivate.lockState = OENLockedState.UNLOCKED;
    this.data.commentClient.lockState = OENLockedState.UNLOCKED;
    this.data.holdSTP.lockState = OENLockedState.UNLOCKED;
    this.data.privateOrder.lockState = OENLockedState.UNLOCKED;
    this.data.customValues.lockState = OENLockedState.UNLOCKED;
    /****************************/
    // OBO
    this.data.oboClient.lockState = OENLockedState.UNLOCKED;
    this.data.oboAccount.lockState = OENLockedState.UNLOCKED;

    this.unlockAll();
  }

  private resetMarkup(): void {
    let markup = (this.data.markup.value = new ICMarkupModel());
    markup.value = null;
    markup.textValue = "";
    markup.typeValue = null;
    markup.checkbox.enabled = true;
    markup.checkbox.value = false;
    this.updateMarkup(this.data.markup.value);
    this.api.updateMarkup(this.refId, this.data.markup.value);
    this.markupInputControl.forEach((c) => c.resetInputControl());
  }

  /**********************************************************************/
  /* EXTERNAL INPUT VALIDATION */

  private handleInvalidInput(inputError: OEInputModel): void {
    if (
      inputError != null &&
      (inputError.panel === OENPanel.PANEL_CLIENT ||
        inputError.panel === OENPanel.PANEL_FINANCIALS ||
        inputError.panel === OENPanel.PANEL_MORE)
    ) {
      switch (inputError.input) {
        /********* CLIENT PANEL *********/
        case OENInputControl.CLIENT:
          // guard added to avoid marking client as invalid on duplicate based on external validation after model synthesis,
          // where the client is null due to visibility, related to OMS-35883
          if (
            this.control.initialLoadingComplete ||
            this.operationMode !== OENOperationMode.DUPLICATE
          ) {
            this.data.client.externalState = ICNInputControlState.INVALID;
          }
          break;
        case OENInputControl.ACCOUNT:
          if (
            this.control.initialLoadingComplete ||
            this.operationMode !== OENOperationMode.DUPLICATE
          ) {
            this.data.account.externalState = ICNInputControlState.INVALID;
          }
          break;
        case OENInputControl.TRADER:
          this.data.clientTrader.externalState = ICNInputControlState.INVALID;
          break;
        /********* FINANCIALS PANEL *********/
        case OENInputControl.CCY_PAIR:
          this.data.currencyPair.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.ALGO_TYPE:
          this.data.algoType.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.CLIENT_INTENT:
          //this.data.clientIntent.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.FIXED_CCY:
          //this.data.clientIntent.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.ORDER_AMOUNT:
          this.data.orderAmount.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.VALUE_DATE:
          this.data.valueDate.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.MARKUP:
          this.data.markup.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.GOOD_FROM:
          if (inputError.value != null && inputError.value !== "") {
            this.data.goodFrom.errorMsg = inputError.value;
          }
          this.data.goodFrom.externalState = ICNInputControlState.INVALID;

          break;
        case OENInputControl.GOOD_UNTIL:
          if (inputError.value != null && inputError.value !== "") {
            this.data.goodUntil.errorMsg = inputError.value;
          }
          this.data.goodUntil.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.SUSPEND:
          this.data.suspend.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.SUSPEND_FROM:
          this.data.suspend.from.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.SUSPEND_TO:
          this.data.suspend.to.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.TRADER_GROUP:
          this.data.traderGroup.state = ICNInputControlState.INVALID;
          break;
        /********* MORE PANEL *********/
        case OENInputControl.CLIENT_COMMENTS:
          this.data.commentClient.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.PRIVATE_COMMENTS:
          this.data.commentPrivate.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.HOLD_STP:
          //this.data.holdSTP.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.EXECUTION_MODE:
          // this.data.execution.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.PRIVATE_ORDER:
          // this.data.privateOrder.externalState = ICNInputControlState.INVALID;
          break;
        case OENInputControl.CUSTOM_FIELDS:
          this.data.customValues.externalState = ICNInputControlState.INVALID;
          break;
      }
    } else {
      logger.out.error(
        `ERROR: oe${
          OENEntryType[this.entryType]
        }Entry.handleInvalidInput(): NULL or Invalid panel: `,
        inputError,
      );
    }
  }
  /**********************************************************************/
  /* LOAD */

  // handleLoadControls
  protected load(data: OEDLoadingDataModel): void {
    if (
      data != null &&
      data instanceof OEDLoadingDataModel &&
      data.values instanceof Array &&
      data.values.length > 0
    ) {
      this.control.isLoading = data.blockPropagation;
      this.control.clientAccountConstraintAutoSelect = false;

      let containsCheck: any = {
        type: false,
        goodFrom: false,
        goodUntil: false,
        //	executionMode: false
      };

      for (let item of data.values) {
        /********* CLIENT PANEL *********/
        if (item.panel === OENPanel.PANEL_CLIENT) {
          switch (item.input) {
            case OENInputControl.CLIENT:
              if (
                typeof item.value === "string" &&
                (item.value === PRIVATIZATION_LABEL || item.value === RESTRICTED_LABEL)
              ) {
                this.icClient.forEach((c) => c.setUIPrivatized(item.value));
              } else {
                this.data.client.value = item.value;
                if (OENOperationMode.MODIFY_INFO === this.operationMode) {
                  this.lockClientForModifyInfo();
                }
                this.icClient.forEach((c) => c.setDataValue(item.value, true, false, true));
                this.icClient.forEach((c) => c.autocompleteValidate());
                this.icClientTrader.forEach((c) => (c.input_constraint = item.value));

                this.propagateClientChange();
              }
              break;
            case OENInputControl.ACCOUNT:
              if (
                typeof item.value === "string" &&
                (item.value === PRIVATIZATION_LABEL || item.value === RESTRICTED_LABEL)
              ) {
                this.icAccount.forEach((c) => c.setUIPrivatized(item.value));
              } else {
                this.data.account.value = item.value;
                if (OENOperationMode.MODIFY_INFO === this.operationMode) {
                  this.lockAccountForModifyInfo();
                }
                setTimeout(() => {
                  this.icAccount.forEach((c) => c.setDataValue(item.value, true, false, true));
                  this.icAccount.forEach((c) => c.autocompleteValidate());
                }, 0);
                setTimeout(() => {
                  this.icAccount.forEach((c) => c.checkAndLockAccountForClient());
                }, 300);
                this.propagateAccountChange();
              }
              break;
            case OENInputControl.CONTACT:
              this.data.contacts.value = item.value;
              break;
            case OENInputControl.TRADER:
              if (typeof item.value === "string" && item.value === PRIVATIZATION_LABEL) {
                this.icClientTrader.forEach((c) => c.setUIPrivatized(item.value));
              } else {
                this.data.clientTrader.value = item.value;
                this.icClientTrader.forEach((c) => c.setDataValue(item.value, true, false, true));
                //this.icClientTrader.autocompleteValidate();
              }
              break;
            default:
              break;
          }
          /********* FINANCIALS PANEL *********/
        } else if (item.panel === OENPanel.PANEL_FINANCIALS) {
          switch (item.input) {
            case OENInputControl.CCY_PAIR:
              this.icCcyPair.forEach((c) => c.load(item.value));
              //this.setCurrencyPair(item.value);
              let comparisonProduct = this.data.previousValue.inverted
                ? this.data.previousValue.product.substr(3, 3) +
                  this.data.previousValue.product.substr(0, 3)
                : this.data.previousValue.product;
              if (
                this.refId === this.rootRefId ||
                this.api.getModifyIssuerRefId(this.refId) === this.refId
              ) {
                // guard valid only for the root, issue OMS-26559; and if this is the starting leg for modify.
                let requestLoadItem = data.values.find((loadItem: OEInputModel) => {
                  return loadItem.input === OENInputControl.LIMIT_PRICE;
                });
                this.data.limitPrice.loadRate =
                  requestLoadItem != null &&
                  requestLoadItem.value != null &&
                  item.value !== comparisonProduct;
              } else {
                this.data.limitPrice.loadRate = false;
              }

              this.onCcyPairBlur(item.value);
              break;
            case OENInputControl.ALGO_TYPE:
              if (item.value instanceof ICAutocompleteGenericModel) {
                const algoModel = item.value;
                const algoId = item.value.value;
                this.initializeAlgoTypeDataset(algoId);
                this.data.algoType.value = algoModel;
                this.propagateAlgoType(algoId);
              } else {
                this.data.algoType.value = null;
              }
              this.cd.markForCheck();
              break;
            case OENInputControl.ORDER_TYPE:
              //not in use

              break;
            case OENInputControl.CLIENT_INTENT:
              this.clientIntentToggleControl.forEach((c) => c.writeValue(item.value));
              break;
            case OENInputControl.FIXED_CCY:
              if (item.value instanceof ICFixedCcyModel || item.value === null) {
                this.fixedCcyControl.forEach((c) => c.load(item.value, false));
                this.data.fixedCcy.ccyPair = item.value.ccyPair;
                this.data.fixedCcy.fixedCcy = item.value.ccy;
              }
              break;
            case OENInputControl.ORDER_AMOUNT:
              if (this.data.orderAmount.value !== item.value) {
                this.data.orderAmount.value = item.value;
                this.orderAmountInputControl.forEach((c) => c.loadValue(item.value));
              }
              break;
            case OENInputControl.LIMIT_PRICE:
              // code below is needed to avoid load overwrite on duplicate, since load() does not trigger change propagation
              if (item.value instanceof ICLimitPriceModel) {
                this.data.limitPrice.value = item.value;
              }
              break;
            case OENInputControl.VALUE_DATE:
              if (item.value instanceof ICTenorValueDateModel) {
                this.data.valueDate.value = item.value;
                this.data.valueDate.isSpot =
                  this.data.valueDate.value &&
                  this.data.valueDate.value.tenor == CubiqTS.Tenor.SPOT;
                this.data.valueDate.isBrokenDate =
                  this.data.valueDate.value &&
                  this.data.valueDate.value.tenor == null &&
                  this.data.valueDate.value.valueDate != null;
                this.valueDateInputControl.forEach((c) => c.writeValue(item.value));
              }
              break;
            case OENInputControl.MARKUP:
              if (item.value instanceof ICMarkupModel) {
                this.data.markup.value = item.value;
                this.markupInputControl.forEach((c) => c.load(item.value));

                // fix for OMS-33516
                if (item.value.checkbox.enabled && item.value.checkbox.value) {
                  this.displayMarkupRetrievalButton();
                }
              }
              break;
            case OENInputControl.GOOD_FROM:
              containsCheck.goodFrom = true;
              this.goodFromInputControl.forEach((c) => c.loadGoodFromUntil(item.value, true));
              break;
            case OENInputControl.GOOD_UNTIL:
              containsCheck.goodUntil = true;
              this.goodUntilInputControl.forEach((c) => c.loadGoodFromUntil(item.value, true));
              break;
            case OENInputControl.SUSPEND:
              this.suspendInputControl.forEach((c) => c.loadSuspendValue(item.value));
              break;
            case OENInputControl.SUSPEND_FROM:
            case OENInputControl.SUSPEND_TO:
              logger.out.error(
                "Individual suspend from/to load not implemented! Load value = ",
                item,
              );
              break;
            case OENInputControl.TRADER_GROUP:
              // CubiqTS.TradingGroupImpl
              if (this.operationMode == OENOperationMode.MODIFY) {
                // If no OCR flag or OCR is false then we enable trader group
                this.data.traderGroup.traderGroupModifiable = !(
                  item.value?.orderCancelReplaceFg ?? false
                );
              }
              if (!this.utility.compareObjects(this.data.traderGroup.value, item.value)) {
                this.data.traderGroup.value = item.value === "" ? null : item.value;
                this.data.traderGroup.traderGroupVisited = true;
                this.onTraderGroupChange();
                this.validateTraderGroup();
              }
              break;
          }
          /********* MORE PANEL *********/
        } else if (item.panel === OENPanel.PANEL_MORE) {
          switch (item.input) {
            case OENInputControl.CLIENT_COMMENTS:
              if (typeof item.value === "string" && item.value === PRIVATIZATION_LABEL) {
                this.data.commentClient.value = item.value;
                this.data.commentClient.privatizationLabel = item.value;
                this.data.commentClient.uiPrivatized = true;
                this.data.commentClient.lockState = OENLockedState.LOCKED;
                this.data.commentClient.locked = true;
              } else if (item.value == null || typeof item.value === "string") {
                this.data.commentClient.value = item.value;
              } else {
                logger.out.error(
                  `ERROR: oe${
                    OENEntryType[this.entryType]
                  }Entry.handleLoadControls(): Invalid client comment value!`,
                );
              }
              break;
            case OENInputControl.PRIVATE_COMMENTS:
              if (typeof item.value === "string" && item.value === PRIVATIZATION_LABEL) {
                this.data.commentPrivate.value = item.value;
                this.data.commentPrivate.privatizationLabel = item.value;
                this.data.commentPrivate.uiPrivatized = true;
                this.data.commentPrivate.lockState = OENLockedState.LOCKED;
                this.data.commentPrivate.locked = true;
              } else if (item.value == null || typeof item.value === "string") {
                this.data.commentPrivate.value = item.value;
                this.onCommentPrivateChange();
              } else {
                logger.out.error(
                  `ERROR: oe${
                    OENEntryType[this.entryType]
                  }Entry.handleLoadControls(): Invalid private comment value!`,
                );
              }
              break;
            case OENInputControl.CUSTOM_FIELDS:
              this.customFields.forEach((c) =>
                c.loadCustomValues(item.value.customValues, item.value.hasStrategyFill),
              );
              break;

            case OENInputControl.HOLD_STP:
              this.icHoldSTP.forEach((c) => c.load(item.value));
              break;
            case OENInputControl.AUTO_FORWARD_PRICING:
              this.icAutoFwdPricing.forEach((c) => c.load(item.value));
              break;
            case OENInputControl.PRIVATE_ORDER:
              if (typeof item.value === "string" && item.value === PRIVATIZATION_LABEL) {
                this.icPrivateOrder.forEach((c) => c.load(true));
                this.data.privateOrder.uiPrivatized = true;
              } else {
                this.icPrivateOrder.forEach((c) => c.load(item.value));
              }
              break;
            case OENInputControl.EXECUTION_MODE:
              break;
            case OENInputControl.PARTIAL_FILL:
              break;
            case OENInputControl.STP_AGG:
              break;
          }
        } else if (item.panel === OENPanel.PANEL_OBO) {
          switch (item.input) {
            case OENInputControl.OBO_CLIENT:
              this.data.oboClient.value = item.value;
              break;
            case OENInputControl.OBO_ACCOUNT:
              this.data.oboAccount.value = item.value;
              break;
          }
        } else {
          logger.out.error(
            `ERROR: oe${
              OENEntryType[this.entryType]
            }Entry.handleLoadControls(): Invalid panel for item: `,
            item,
          );
        }
      }

      if (!data.singlePropagation) {
        this.postLoad(containsCheck);
      }

      // set account auto-select on constraint (added because of OMS-35837)
      setTimeout(() => {
        this.control.clientAccountConstraintAutoSelect = true;
      }, 100);
      // setting flag that indicates that data has been loaded at least once
      setTimeout(() => {
        this.control.initialLoadingComplete = true;
      }, 500);
    } else {
      setTimeout(() => {
        this.control.isLoading = false;
      });
      logger.out.error(
        `ERROR: oe${OENEntryType[this.entryType]}Entry.handleLoadControls(): Data is NULL: `,
        data,
      );
    }
  }

  private lockAccountForModifyInfo() {
    this.data.account.lockState = OENLockedState.LOCKED;
    this.icAccount.forEach((c) => c.lock());
  }

  private lockClientForModifyInfo() {
    this.data.client.lockState = OENLockedState.LOCKED;
    this.icClient.forEach((c) => c.lock());
  }

  private postLoad(containsCheck: any): void {
    /********* CLIENT PANEL *********/
    this.contactsInputControl.forEach((contactControl) => {
      contactControl.writeClient(this.data.client.value);
      contactControl.writeClientTrader(this.data.clientTrader.value);
      contactControl.writeValue(this.data.contacts.value);
      contactControl.loadComplete();
    });

    this.cd.markForCheck();

    setTimeout(() => {
      this.control.isLoading = false;
      this.checkForCalendar();
    });
  }

  /**********************************************************************/
  /* LOCKING */

  private lockAll(): void {
    this.control.lockAllFg = true;
    this.updateLocking();
  }

  private unlockAll(): void {
    this.control.lockAllFg = false;
    this.updateLocking();
  }

  protected loadLockProfile(lockingProfile: OELockingProfileTransportModel): void {
    if (lockingProfile instanceof OELockingProfileTransportModel) {
      for (let lockStateItem of lockingProfile.profile) {
        if (
          lockStateItem.panel === OENPanel.PANEL_CLIENT ||
          lockStateItem.panel === OENPanel.PANEL_FINANCIALS ||
          lockStateItem.panel === OENPanel.PANEL_MORE ||
          lockStateItem.panel === OENPanel.PANEL_OBO
        ) {
          this.assignLockStateToData(lockStateItem);
        }
      }
      if (lockingProfile.profileType === OENLockingProfileType.PERSISTENT) {
        this.control.lockingProfileData = lockingProfile.profile;
      }
      this.updateLocking();
    } else {
      logger.out.warn(`WARNING: oeBmarkPanel.loadLockProfile(): Invalid locking profile`);
    }
  }

  private assignLockStateToData(lockStateItem: OEInputLockStateItemModel): void {
    switch (lockStateItem.input) {
      /********* CLIENT PANEL *********/
      case OENInputControl.CLIENT:
        this.data.client.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.ACCOUNT:
        this.data.account.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.CONTACT:
        this.data.contacts.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.TRADER:
        this.data.clientTrader.lockState = lockStateItem.lockState;
        break;
      /********* FINANCIALS PANEL *********/
      case OENInputControl.ACCOUNT_ACTIVE_TAB:
      case OENInputControl.CLIENT_ACTIVE_TAB:
        // Do nothing, not in use for locking
        break;
      case OENInputControl.CCY_PAIR:
        this.data.currencyPair.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.ALGO_TYPE:
        this.data.algoType.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.CLIENT_INTENT:
        this.data.clientIntent.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.FIXED_CCY:
        this.data.fixedCcy.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.ORDER_AMOUNT:
        this.data.orderAmount.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.VALUE_DATE:
        this.data.valueDate.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.MARKUP:
        this.data.markup.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.GOOD_FROM:
        this.data.goodFrom.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.GOOD_UNTIL:
        this.data.goodUntil.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.SUSPEND:
        this.data.suspend.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.SUSPEND_FROM:
      case OENInputControl.SUSPEND_TO:
        // Do nothing, not in use for locking
        break;
      case OENInputControl.TRADER_GROUP:
        this.data.traderGroup.lockState = lockStateItem.lockState;
        break;
      // SPOT Locking
      case OENInputControl.ORDER_TYPE:
      case OENInputControl.VALUE_DATE:
        break;
      /********* MORE PANEL *********/
      case OENInputControl.CLIENT_COMMENTS:
        this.data.commentClient.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.PRIVATE_COMMENTS:
        this.data.commentPrivate.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.CUSTOM_FIELDS:
        this.data.customValues.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.EXECUTION_MODE:
        break;
      case OENInputControl.PRIVATE_ORDER:
        this.data.privateOrder.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.HOLD_STP:
        this.data.holdSTP.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.PARTIAL_FILL:
        break;
      case OENInputControl.STP_AGG:
        break;
      case OENInputControl.OBO_CLIENT:
        this.data.oboClient.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.OBO_ACCOUNT:
        this.data.oboAccount.lockState = lockStateItem.lockState;
        break;
      case OENInputControl.REQUEST_PRICE:
      case OENInputControl.TRIGGER:
      case OENInputControl.MARKUP_TYPE:
      case OENInputControl.FIXING_SOURCE:
      case OENInputControl.FIXING_DATE:
      case OENInputControl.AUTO_FORWARD_PRICING:
      case OENInputControl.DELTA:
      case OENInputControl.RUNGS_ABOVE:
      case OENInputControl.RUNGS_BELOW:
      case OENInputControl.TRAILING_PIPS:
      case OENInputControl.TRAILING_TRIGGER:
        // THese are not supported in Algos but here to reduce error logging
        break;
      default:
        logger.out.info(
          `ERROR: oe${
            OENEntryType[this.entryType]
          }Entry.handleLoadLockProfile(): unidentified lock item: ${
            OENInputControl[lockStateItem.input]
          }`,
          lockStateItem,
        );
        break;
    }
  }

  protected updateLocking(): void {
    if (!this.control.lockAllFg) {
      // lockAll is INACTIVE
      //client
      if (this.data.client.lockState === OENLockedState.LOCKED) {
        this.icClient.forEach((c) => c.lock());
      } else if (this.data.client.lockState === OENLockedState.UNLOCKED) {
        this.icClient.forEach((c) => c.unlock());
      }
      // account
      if (this.data.account.lockState === OENLockedState.LOCKED) {
        this.icAccount.forEach((c) => c.lock());
      } else if (this.data.account.lockState === OENLockedState.UNLOCKED) {
        this.icAccount.forEach((c) => c.unlock());
      }
      // contact
      if (this.data.contacts.lockState === OENLockedState.LOCKED) {
        this.contactsInputControl.forEach((c) => c.lockInputControl());
      } else if (this.data.contacts.lockState === OENLockedState.UNLOCKED) {
        this.contactsInputControl.forEach((c) => c.unlockInputControl());
      }
      // client trader
      if (this.data.clientTrader.lockState === OENLockedState.LOCKED) {
        this.icClientTrader.forEach((c) => c.lock());
      } else if (this.data.clientTrader.lockState === OENLockedState.UNLOCKED) {
        this.icClientTrader.forEach((c) => c.unlock());
      }
      // CCY_PAIR:
      if (this.data.currencyPair.lockState === OENLockedState.LOCKED) {
        this.icCcyPair.forEach((c) => c.lock());
      } else if (this.data.currencyPair.lockState === OENLockedState.UNLOCKED) {
        this.icCcyPair.forEach((c) => c.unlock());
      }
      // ORDER_TYPE:
      if (this.data.algoType.lockState === OENLockedState.LOCKED) {
        this.icAlgoType.forEach((c) => c.lock());
      } else if (this.data.algoType.lockState === OENLockedState.UNLOCKED) {
        this.icAlgoType.forEach((c) => c.unlock());
      }
      // CLIENT_INTENT:
      if (this.data.clientIntent.lockState === OENLockedState.LOCKED) {
        this.clientIntentToggleControl.forEach((c) => c.lockInputControl());
      } else if (this.data.clientIntent.lockState === OENLockedState.UNLOCKED) {
        this.clientIntentToggleControl.forEach((c) => c.unlockInputControl());
      }
      // FIXED_CCY:
      if (this.data.fixedCcy.lockState === OENLockedState.LOCKED) {
        this.fixedCcyControl.forEach((c) => c.lock());
      } else if (this.data.fixedCcy.lockState === OENLockedState.UNLOCKED) {
        this.fixedCcyControl.forEach((c) => c.unlock());
      }
      // ORDER_AMOUNT:
      if (this.data.orderAmount.lockState === OENLockedState.LOCKED) {
        this.orderAmountInputControl.forEach((c) => c.lockInputControl());
      } else if (this.data.orderAmount.lockState === OENLockedState.UNLOCKED) {
        this.orderAmountInputControl.forEach((c) => c.unlockInputControl());
      }
      // VALUE_DATE:
      if (this.data.valueDate.lockState === OENLockedState.LOCKED) {
        this.valueDateInputControl.forEach((c) => c.lockInputControl());
      } else if (this.fwdEnabled && this.data.valueDate.lockState === OENLockedState.UNLOCKED) {
        this.valueDateInputControl.forEach((c) => c.unlockInputControl());
      }
      // MARKUP:
      if (this.data.markup.lockState === OENLockedState.LOCKED) {
        this.markupInputControl.forEach((c) => c.lockInputControl());
        //	this.data.tracking.locked = true;
      } else if (this.data.markup.lockState === OENLockedState.UNLOCKED) {
        this.markupInputControl.forEach((c) => c.unlockInputControl());
        //	this.data.tracking.locked = false;
      }
      // GOOD FROM:
      if (this.data.goodFrom.lockState === OENLockedState.LOCKED) {
        // this.data.goodFrom.locked = true;
        this.goodFromInputControl.forEach((c) => c.lockInputControl());
      } else if (this.data.goodFrom.lockState === OENLockedState.UNLOCKED) {
        // this.data.goodFrom.locked = false;
        this.goodFromInputControl.forEach((c) => c.unlockInputControl());
      }
      // GOOD UNTIL:
      if (this.data.goodUntil.lockState === OENLockedState.LOCKED) {
        // this.data.goodUntil.locked = true;
        this.goodUntilInputControl.forEach((c) => c.lockInputControl());
      } else if (this.data.goodUntil.lockState === OENLockedState.UNLOCKED) {
        // this.data.goodUntil.locked = false;
        this.goodUntilInputControl.forEach((c) => c.unlockInputControl());
      }
      // SUSPEND :
      if (this.data.suspend.lockState === OENLockedState.LOCKED) {
        this.suspendInputControl.forEach((c) => c.lockInputControl());
      } else if (this.data.suspend.lockState === OENLockedState.UNLOCKED) {
        this.suspendInputControl.forEach((c) => c.unlockInputControl());
      }
      // TRADER GROUP
      if (this.data.traderGroup.lockState === OENLockedState.LOCKED) {
        this.data.traderGroup.locked = true;
      } else if (this.data.traderGroup.lockState === OENLockedState.UNLOCKED) {
        this.data.traderGroup.locked = false;
      }

      // PRIVATE COMMENTS
      if (this.data.commentPrivate.uiPrivatized) {
        // privatization locked (lock state is lower priority)
        this.data.commentPrivate.locked = true;
      } else if (this.data.commentPrivate.lockState === OENLockedState.LOCKED) {
        this.data.commentPrivate.locked = true;
      } else if (this.data.commentPrivate.lockState === OENLockedState.UNLOCKED) {
        if (this.rootRefId != this.refId && !this.settings.independentComments) {
          this.data.commentPrivate.locked = true;
        } else {
          this.data.commentPrivate.locked = false;
        }
      } else {
        this.data.commentPrivate.locked = false;
        logger.out.error(`Invalid MoreDetails->Private Comment lock state!`); // remove after dev testing
      }
      // CLIENT COMMENTS:
      if (this.data.commentClient.uiPrivatized) {
        // privatization locked (lock state is lower priority)
        this.data.commentClient.locked = true;
      } else if (this.data.commentClient.lockState === OENLockedState.LOCKED) {
        this.data.commentClient.locked = true;
      } else if (this.data.commentClient.lockState === OENLockedState.UNLOCKED) {
        if (this.rootRefId != this.refId && !this.settings.independentComments) {
          this.data.commentClient.locked = true;
        } else {
          this.data.commentClient.locked = false;
        }
      } else {
        this.data.commentClient.locked = false;
        logger.out.error(`Invalid MoreDetails->Client Comment lock state!`);
      }
      // Custom Fields
      if (this.data.customValues.lockState === OENLockedState.LOCKED) {
        this.customFields.forEach((c) => c.lockInputControl());
      } else if (this.data.customValues.lockState === OENLockedState.UNLOCKED) {
        this.customFields.forEach((c) => c.unlockInputControl());
      }
      // HOLD STP
      if (this.data.holdSTP.lockState === OENLockedState.LOCKED) {
        this.icHoldSTP.forEach((c) => c.lock());
      } else if (this.data.holdSTP.lockState === OENLockedState.UNLOCKED) {
        this.icHoldSTP.forEach((c) => c.unlock());
      }

      // AUTO FWD
      if (this.data.autoFwdPricing.lockState === OENLockedState.LOCKED) {
        this.icAutoFwdPricing.forEach((c) => c.lock());
      } else if (this.data.autoFwdPricing.lockState === OENLockedState.UNLOCKED) {
        this.icAutoFwdPricing.forEach((c) => c.unlock());
      }

      // PRIVATE ORDER
      if (this.data.privateOrder.uiPrivatized) {
        // privatization locked
        this.icPrivateOrder.forEach((c) => c.lock());
      } else if (this.data.privateOrder.lockState === OENLockedState.LOCKED) {
        this.icPrivateOrder.forEach((c) => c.lock());
      } else if (this.data.privateOrder.lockState === OENLockedState.UNLOCKED) {
        this.icPrivateOrder.forEach((c) => c.unlock());
      }

      // OBO
      this.data.oboClient.disabled = this.data.oboClient.lockState === OENLockedState.LOCKED;
      this.data.oboAccount.disabled = this.data.oboAccount.lockState === OENLockedState.LOCKED;
    } else {
      // lockAll is ACTIVE
      // CLIENT
      this.icClient.forEach((c) => c.lock());
      this.icAccount.forEach((c) => c.lock());
      this.icClientTrader.forEach((c) => c.lock());
      this.contactsInputControl.forEach((c) => c.lockInputControl());
      // FINANCIALS
      this.icCcyPair.forEach((c) => c.lock());
      this.icAlgoType.forEach((c) => c.lock());
      this.clientIntentToggleControl.forEach((c) => c.lockInputControl());
      this.fixedCcyControl.forEach((c) => c.lock());
      this.orderAmountInputControl.forEach((c) => c.lockInputControl());
      // this.icLimitPrice.forEach((c) => c.lock());
      this.valueDateInputControl.forEach((c) => c.lockInputControl());
      this.markupInputControl.forEach((c) => c.lockInputControl());
      this.goodFromInputControl.forEach((c) => c.lockInputControl());
      this.goodUntilInputControl.forEach((c) => c.lockInputControl());
      this.suspendInputControl.forEach((c) => {
        c.lockInputControl();
      });
      this.data.traderGroup.locked = true;
      //	this.data.tracking.locked = true;
      // MORE
      this.data.commentPrivate.locked = true;
      this.data.commentClient.locked = true;
      this.data.customValues.locked = true;
      this.icHoldSTP.forEach((c) => c.lock());
      this.icAutoFwdPricing.forEach((c) => c.lock());
      this.icPrivateOrder.forEach((c) => c.lock());
      this.customFields.forEach((c) => c.lockInputControl());
      // OBO
      this.data.oboClient.disabled = true;
      this.data.oboAccount.disabled = true;
    }
  }

  /**********************************************************************/
  /* DATASET  */
  /* 	private orderTypeGetDataset(applyToInterface: boolean  = true): void {
		let result = this.api.loadOrderTypeDataset(this.refId);
		if (result != null && result instanceof Array) {
			this.data.orderType.dataset = result;//.slice();
			if(applyToInterface){
				this.icOrderType.forEach(c => c.initializeOnChanges());
			}
		} else {
			logger.out.error("ERROR: oeSpotEntry.orderTypeGetDataset(): Order types dataset load failed!");
			this.data.orderType.dataset = [];
		}
	} */

  /**
   * Initialise the tradable algos - if we have loaded in any algo type, then we must also include that in the dropdown list
   * to enable the control to be validate correctly
   * @param modifyAlgoId Optional algo Id when modifying an existing algo order
   */
  private initializeAlgoTypeDataset(modifyAlgoId?: string) {
    const algos = this.api.loadTradableAlgos(modifyAlgoId);
    this.data.algoType.dataset = algos;
    // check
    if (!(this.data.algoType.dataset instanceof Array && this.data.algoType.dataset.length > 0)) {
      logger.out.error(
        "ERROR: oeAlgoEntry.initializeAlgoTypeDataset(): Algo types dataset load failed!",
      );
    }
  }

  private displayMarkupRetrievalButton(): void {
    this.updateRetrieveMarkupEnabled();

    if (
      this.data.markup.config.markupRetrievalEnabled &&
      this.data.markup.config.markupType !== "MANUAL"
    ) {
      this.markupInputControl.forEach((c) => c.displayMarkupRefresh(true));
    }
  }

  /**
   * Load all currency pairs from the replicate provided base lookup service
   * and fill in the appropriate currency pair values (inverted too, where applicable)
   * in the data model
   */
  private currencyPairsGetDataset(): void {
    let dataset =
      this.entryType === OENEntryType.NDF_ALGO
        ? this.api.getNDFAlgoCcyPairsDataset()
        : this.api.getAlgoCcyPairsDataset();

    if (dataset != null && dataset instanceof Array && dataset.length > 0) {
      this.data.currencyPair.dataset = dataset.slice();
    } else {
      logger.out.error(
        `ERROR: oe${
          OENEntryType[this.entryType]
        }Entry.currencyPairsGetDataset(): CcyPairs dataset load failed!`,
      );
      this.data.currencyPair.dataset = [];
    }
  }

  /**********************************************************************/
  /* CONTACTS MANAGER - CLIENT  */

  public onClientDefaultCommentsChanged($event: any): void {
    // updating client default comments overrides previously set independent comments
    if ($event.hasClientTraderComment) {
      this.api.updateClientTraderDefaultComment(this.refId, $event.clientTraderComment);
      this.api.updateClientDefaultComments(this.refId, $event.clientComment, $event.privateComment);
    } else {
      this.api.updateClientDefaultComments(
        this.refId,
        this.data.client.value.defaultClientComment,
        this.data.client.value.defaultPrivateComment,
      );
      this.propagateClientDefaultCommentsChange($event);
    }
  }

  public onClientTraderUpdate($event: any): void {
    this.data.clientTrader.value = $event;

    this.cd.markForCheck();
    if (this.icClientTrader != null) {
      // OMS-23843
      this.icClientTrader.forEach((c) =>
        c.setDataValue(this.data.clientTrader.value, true, false, true),
      );
      this.icClientTrader.forEach((c) => c.autocompleteValidate());
    }

    this.api.updateClientTrader(this.refId, this.data.clientTrader.value);
  }

  public onContactManagerOpen($event: any): void {
    this.api.cmdFreezeInterfaceControls(this.refId);
  }

  public onContactManagerClosed($event: any): void {
    this.api.cmdUnfreezeInterfaceControls(this.refId);
  }

  /**********************************************************************/
  /* CONTROL SETTERS - CLIENT  */

  public onClientChange($event: any): void {
    this.cd.markForCheck();

    this.api.updateClient(this.refId, this.data.client.value);
    this.updateRetrieveMarkupEnabled();
  }

  public onClientSelect($event: any): void {
    let clientId = $event;
    this.oeLogger.outputDevLogToConsole("PANEL Client PREDICTED VALUE: ", false, $event);
    this.evaluateMarkupByBranch(clientId);
  }

  public onAccountChange($event: any): void {
    this.api.updateAccount(this.refId, this.data.account.value);
    this.updateRetrieveMarkupEnabled();
  }

  public onContactsChange($event: any): void {
    this.data.contacts.value = $event;
    this.api.updateContacts(this.refId, this.data.contacts.value);
    // propagate
    this.propagateClientChange();
  }

  public onClientTraderChange($event: any): void {
    this.api.updateClientTrader(this.refId, this.data.clientTrader.value);
  }

  /**********************************************************************/
  /* CONTROL SETTERS - FINANCIALS  */

  public onCcyPairBlur($event: any): void {
    let ccyPairIsNew: boolean = this.setCurrencyPair($event);
    if (ccyPairIsNew) {
      this.api.updateCcyPair(
        this.refId,
        this.data.currencyPair.product,
        this.data.currencyPair.model,
      );
      ///
      this.updateRetrieveMarkupEnabled();
    }
  }

  public onAlgoTypeChange($event: any): void {
    this.propagateAlgoType(
      this.data.algoType.value != null ? this.data.algoType.value.value : null,
    );
    this.api.updateAlgoType(this.refId, this.data.algoType.value);
    this.updateRetrieveMarkupEnabled();
  }

  public onClientIntentToggleChange($event: any): void {
    this.api.updateClientIntent(this.refId, this.data.clientIntent.value);
    this.updateRetrieveMarkupEnabled();
  }

  public onFixedCcyToggleChange($event: any): void {
    if (
      this.data.fixedCcy.value != null &&
      typeof this.data.fixedCcy.value.ccy === "string" &&
      this.data.fixedCcy.value.ccy.length === 3 &&
      this.data.fixedCcy.fixedCcy !== this.data.fixedCcy.value.ccy
    ) {
      this.data.fixedCcy.fixedCcy = this.data.fixedCcy.value.ccy;
    } else {
      this.data.fixedCcy.fixedCcy = null;
    }

    this.api.updateFixedCcy(this.refId, this.data.fixedCcy.value);
    this.updateRetrieveMarkupEnabled();
  }

  public onOrderAmountChange($event: any): void {
    this.api.updateOrderAmount(this.refId, this.data.orderAmount.value);
  }

  public onOrderAmountBlur($event: any): void {
    let orderAmountUserValue = new CubiqTS.BigNumber(this.control.multiplyAmountUserValue);
    if (
      this.data.orderAmount.value != null &&
      this.data.orderAmount.value.isLessThan(orderAmountUserValue) &&
      this.control.multiplyAmountUserToggle === true
    ) {
      this.data.orderAmount.value = this.data.orderAmount.value.times(1000000);
    }
    this.api.updateOrderAmount(this.refId, this.data.orderAmount.value);
    //
    this.updateRetrieveMarkupEnabled();
  }

  public onLimitPriceChange($event: any): void {
    this.api.updateLimitPrice(this.refId, this.data.limitPrice.value);
    this.updateRetrieveMarkupEnabled();
  }

  public onValueDateChange($event: any): void {
    this.data.valueDate.isSpot =
      this.data.valueDate.value && this.data.valueDate.value.tenor == CubiqTS.Tenor.SPOT
        ? true
        : false;
    this.data.valueDate.isBrokenDate =
      this.data.valueDate.value &&
      this.data.valueDate.value.tenor == null &&
      this.data.valueDate.value.valueDate != null
        ? true
        : false;
    this.api.updateValueDate(this.refId, this.data.valueDate.value);
  }

  public onMarkupChange($event: any): void {
    if (
      this.data.markup.value instanceof ICMarkupModel &&
      this.data.markup.value !== null &&
      this.data.markup.config.markupEnabled &&
      this.data.markup.config.markupEntry
    ) {
      this.api.updateMarkup(this.refId, this.data.markup.value);
    }
  }

  public onMarkupCheckboxState(state: boolean): void {
    this.markupCheckboxEnabled = state;
    if (state) {
      if (this.data.markup.config.markupEntry) {
        // markupLimitCbx - if enabled sets markup to 0 Pips
        if (!this.data.markup.value) {
          this.data.markup.value = new ICMarkupModel();
          this.data.markup.value.checkbox.value = state;
          this.data.markup.value.checkbox.enabled = this.data.markup.config.markupEntry;
        }
        let markup = this.data.markup.value;
        let checkSelected = markup.checkbox.enabled == true && markup.checkbox.value == true;
        if (this.data.markup.config.markupCheckboxSelected || checkSelected) {
          // Set default values
          markup.value = BIG_NUMBER_ZERO;
          markup.textValue = BIG_NUMBER_ZERO.toString();
          markup.typeValue = new ICAutocompleteGenericModel(CubiqTS.MarkUpType.Pips, "Pips");
          markup.checkbox.enabled = true;
          markup.checkbox.value = true;
        }

        this.updateMarkup(markup);
        this.displayMarkupRetrievalButton();
        this.api.updateMarkup(this.refId, markup);
      }
      //this.updateTracking();
    } else {
      this.resetMarkup();
    }
  }

  public onSuspendChange($event: any): void {
    this.api.updateSuspend(this.refId, this.data.suspend.value);
  }

  public onGoodFromChange(): void {
    this.data.goodFrom.errorMsg = null;
    this.data.goodFrom.externalState = null;
    this.api.updateGoodFrom(this.refId, this.data.goodFrom.value);

    if (this.data.goodUntil?.value != null) {
      this.goodUntilInputControl?.first?.validate();
    }

    this.cd.markForCheck();
  }

  public onGoodUntilChange(): void {
    this.data.goodFrom.errorMsg = null;
    this.data.goodFrom.externalState = null;
    this.api.updateGoodUntil(this.refId, this.data.goodUntil.value);

    if (this.data.goodFrom?.value != null) {
      this.goodFromInputControl?.first?.validate();
    }

    this.cd.markForCheck();
  }

  public onTraderGroupChange(): void {
    this.api.updateTraderGroup(this.refId, this.data.traderGroup.value);
  }

  /**********************************************************************/
  /* CONTROL -> MODEL SETTERS - MORE PANEL */

  public onCommentPrivateChange(): void {
    if (!this.data.commentPrivate.uiPrivatized) {
      this.api.updatePrivateComment(this.refId, this.data.commentPrivate.value);
    }
  }

  public onCommentClientChange(): void {
    if (!this.data.commentClient.uiPrivatized) {
      this.api.updateClientComment(this.refId, this.data.commentClient.value);
    }
  }

  public onCustomValuesChange(values: Array<CubiqTS.CustomValue>): void {
    this.data.customValues.value = values;
    this.api.updateCustomFields(this.refId, this.data.customValues.value);
  }

  public onCommentClientCheckboxChange(): void {
    setTimeout(() => {
      if (
        typeof this.data.commentClient.checkbox.value === "boolean" &&
        this.data.commentClient.checkbox.value
      ) {
        this.cd.markForCheck();

        if (
          this.data.commentClient.externalState === ICNInputControlState.INVALID &&
          this.data.commentClient.checkbox.value
        ) {
          this.data.commentClient.externalState = null;
        }

        this.icCommentClient.nativeElement.focus();
      }
    }, 0);
  }

  /*    public onExecutionModeChange(): void {
		this.api.updateExecutionMode(this.refId, this.data.executionMode.value);
		setTimeout(()=>{ // invocation of updateTraderGroups() made asynchronous due to Synchronous event broadcasting which causes ExpressionChangedAfterItHasBeenCheckedError
			this.updateTraderGroups();
		},0);
    } */

  public onHoldSTPChange(): void {
    this.api.updateHoldSTP(this.refId, this.data.holdSTP.value);
  }

  public onAutoFwdPricingChange(): void {
    logger.out.info("Got Auto fwd price change: " + this.data.autoFwdPricing.value);
    this.api.updateAutoForwardPrice(this.refId, this.data.autoFwdPricing.value);
  }

  public onPrivateOrderChange(): void {
    if (!this.data.privateOrder.uiPrivatized) {
      this.api.updatePrivateOrder(this.refId, this.data.privateOrder.value);
    }
  }
  /**********************************************************************/
  /* GOOD FROM/UNTIL METHODS */

  /**********************************************************************/
  /* TRADER GROUP METHODS */

  /**
   * Loads and populates lists of trader groups
   */
  protected loadTraderGroups(): void {
    let lookup: TraderGroupLookupService = this.traderGroupService.getBase();
    let all: Array<CubiqTS.TradingGroupImpl> = lookup.getAll(); //.filter((traderGroup: CubiqTS.TradingGroupImpl) => traderGroup.groupId.toNumber() >= 0);
    if (all != null && all instanceof Array && all.length > 0) {
      all = all.filter(
        (traderGroup: CubiqTS.TradingGroupImpl) => traderGroup.groupId.toNumber() >= 0,
      );

      all.forEach((value: CubiqTS.TradingGroupImpl) => (value["id"] = value.groupId.toString()));
      all.sort((a: CubiqTS.TradingGroupImpl, b: CubiqTS.TradingGroupImpl) =>
        a.groupCode.toLowerCase().localeCompare(b.groupCode.toLowerCase()),
      );

      this.data.traderGroup.allTraderGroups = all;
      this.data.traderGroup.benchmarkTraderGroups = all.filter(
        (tradingGroup: CubiqTS.TradingGroupImpl) => tradingGroup.isBenchmarkFg,
      );
      this.data.traderGroup.limitTraderGroups = all.filter(
        (tradingGroup: CubiqTS.TradingGroupImpl) => !tradingGroup.isBenchmarkFg,
      );
      this.data.traderGroup.manualTraderGroups = this.data.traderGroup.limitTraderGroups.filter(
        (value: CubiqTS.TradingGroupImpl) => !value.isAutoFilledFg,
      );
      this.data.traderGroup.limitTraderGroups = this.data.traderGroup.limitTraderGroups.filter(
        (value: CubiqTS.TradingGroupImpl) => value.isAutoFilledFg,
      );
      this.data.traderGroup.algoTraderGroups = this.data.traderGroup.limitTraderGroups.filter(
        (value: CubiqTS.TradingGroupImpl) =>
          (value.isAutoFilledFg && value.isExternallyFilledFg) || value.isStagingFg,
      );
    } else {
      this.data.traderGroup.allTraderGroups = [];
      this.data.traderGroup.benchmarkTraderGroups = [];
      this.data.traderGroup.limitTraderGroups = [];
      this.data.traderGroup.manualTraderGroups = [];
      this.data.traderGroup.algoTraderGroups = [];
    }
  }

  /**
   * Searches the trader group suggestions for a infix matching of the given filter string
   * @param filter the filter should be checked to be an infix of the target trader group
   */
  public searchTraderGroup(filter: any): void {
    let traderGroups: Array<CubiqTS.TradingGroupImpl> = this.getTraderGroups();

    let query: string = filter.query.toLowerCase();
    if (query.length > 0) {
      this.data.traderGroup.traderGroups = traderGroups.filter((tradeGroup: any) =>
        tradeGroup.groupCode.toLowerCase().includes(query),
      );
    } else {
      this.data.traderGroup.traderGroups = traderGroups.slice(0);
    }
  }

  public onSelectTraderGroup(data: any): void {
    this.data.traderGroup.traderGroupVisited = true;
    // empty string correction
    this.data.traderGroup.value =
      this.data.traderGroup.value === "" ? null : this.data.traderGroup.value;
    this.validateTraderGroup();
    this.onTraderGroupChange();
  }

  public traderGroupFocus(): void {
    this.data.traderGroup.traderGroupVisited = true;
  }

  public validateTraderGroup(): void {
    let traderGroups: Array<CubiqTS.TradingGroupImpl> = this.getTraderGroups();

    if (this.data.traderGroup.value != null && typeof this.data.traderGroup.value === "string") {
      let code: string = (this.data.traderGroup.value as string).toLowerCase();
      let traderGroup: CubiqTS.TradingGroupImpl = traderGroups.find(
        (group: CubiqTS.TradingGroupImpl) => group.groupCode.toLowerCase() == code,
      );

      if (traderGroup != null) {
        this.data.traderGroup.value = traderGroup;
      } else if (code.trim().length == 0) {
        this.data.traderGroup.value = null;
      }
    }

    if (this.data.traderGroup.value === null || !this.data.traderGroup.traderGroupVisited) {
      this.data.traderGroup.state = ICNInputControlState.INACTIVE;
    } else {
      let valid: boolean = this.data.traderGroup.value instanceof CubiqTS.TradingGroupImpl;
      this.data.traderGroup.state = valid
        ? ICNInputControlState.VALID
        : ICNInputControlState.INVALID;
    }
  }

  private getTraderGroups(): Array<CubiqTS.TradingGroupImpl> {
    let groups: Array<CubiqTS.TradingGroupImpl> = this.data.traderGroup.allTraderGroups;

    if (
      this.entryType === OENEntryType.ALGO ||
      this.entryType === OENEntryType.NDF_ALGO ||
      (this.data.orderType != null && this.data.orderType.value === CubiqTS.RequestOrderType.ALGO)
    ) {
      groups = this.data.traderGroup.algoTraderGroups;
    } else if (
      this.data.orderType != null &&
      this.data.orderType.value === CubiqTS.RequestOrderType.BMARK
    ) {
      groups = this.data.traderGroup.benchmarkTraderGroups;
    } else {
      // assume execution type is AUTO, this point should not be reached for algos
      groups = this.data.traderGroup.limitTraderGroups.concat(
        this.data.traderGroup.manualTraderGroups,
      );
      /* 	if (this.data.executionMode.value === CubiqTS.ExecutionType.AUTOMATIC) {
				groups = this.data.traderGroup.limitTraderGroups.concat(this.data.traderGroup.manualTraderGroups);
			}else if(this.data.executionMode.value === CubiqTS.ExecutionType.MANUAL){
				groups =  this.data.traderGroup.manualTraderGroups;
			}else{
				logger.out.warn("WARNING: oeSpotEntry.getTraderGroups(): Execution mode not specified! executionMode:", this.data.executionMode.value);
				groups = this.data.traderGroup.limitTraderGroups.concat(this.data.traderGroup.manualTraderGroups);
			} */
    }
    return groups;
  }

  /**********************************************************************/
  /* CONSTRAINT PROPAGATION */

  private propagateClientChange(): void {
    /// execution mode
    //this.data.executionMode.show = this.data.executionMode.showConfig;
    // comments
    if (
      (this.data.client.value != null &&
        this.data.previousValue.clientId !== this.data.client.value.clientId) ||
      (this.data.client.value == null && this.data.previousValue.client != null)
    ) {
      // _client is different
      if (this.data.client.value != null) {
        // client id has changed- set default
        let clientComment = this.api.getClientComment(this.refId);
        this.data.commentClient.value =
          clientComment && !this.data.commentClient.uiPrivatized
            ? clientComment
            : this.data.commentClient.uiPrivatized
              ? this.data.commentClient.privatizationLabel
              : "";
        this.data.commentPrivate.value =
          this.data.client.value.defaultPrivateComment != null &&
          !this.data.commentPrivate.uiPrivatized
            ? this.data.client.value.defaultPrivateComment
            : this.data.commentPrivate.uiPrivatized
              ? this.data.commentPrivate.privatizationLabel
              : "";
        this.onCommentPrivateChange();
      }
    }

    ///
    this.data.previousValue.client = this.data.client.value;
    this.data.previousValue.clientId =
      this.data.previousValue.client != null ? this.data.previousValue.client.clientId : null;

    if (this.data.previousValue.client != null) {
      this.evaluateMarkupByBranch(this.data.previousValue.client.clientId);
    }
  }

  private propagateAlgoType(_: string): void {
    /* handled internally */
  }

  public handleAlgoParamsError($event: boolean): void {
    if (typeof $event === "boolean" && $event) {
      this.initializeAlgoTypeDataset();
      logger.out.warn(
        "WARNING: AlgoType dataset reinitialized due to error in Algo Strategy Panel!",
      );
      setTimeout(() => {
        this.icAlgoType.forEach((c) => c.validate());
      }, 50);
    }
  }

  private propagateAccountChange(): void {
    this.updateRetrieveMarkupEnabled();
  }

  public propagateClientDefaultCommentsChange(data: any): void {
    this.data.commentClient.value = this.data.commentClient.uiPrivatized
      ? this.data.commentClient.privatizationLabel
      : data.clientComment;
    this.data.commentPrivate.value = this.data.commentPrivate.uiPrivatized
      ? this.data.commentPrivate.privatizationLabel
      : data.privateComment;
    //this.onCommentClientChange();
    // this.onCommentPrivateChange();
  }

  /**********************************************************************/
  /* RATE SUBSCRIPTION  */

  /**
   * Convenience method for setting the current currency pair of
   * the model. The method sets up the dependent elements in the model
   * as well.
   * @param productCode the product code (base + nonBase, or invert)
   * @returns true if the currency pair was found and setup, false otherwise
   */
  protected setCurrencyPair(productCode: string): boolean {
    if (
      !this.control.isLoading &&
      !this.data.markup.config.markupEntry &&
      this.control.viewRenderComplete &&
      this.operationMode !== OENOperationMode.MONITOR_EXECUTION
    ) {
      this.updateMarkup(null);
      this.api.updateMarkup(this.refId, null);
    }
    this.data.currencyPair.product = productCode;
    this.data.fixedCcy.ccyPair = productCode;

    if (this.data.previousValue.product !== productCode) {
      if (this.data.currencyPair.product != null) {
        let currencyPairLookup: CcyPairLookupService = this.currencyPairService.getBase();
        this.data.currencyPair.model = currencyPairLookup.getItem(productCode);
        this.data.currencyPair.inverted = false;
        this.data.currencyPair.base = this.data.currencyPair.product.substr(0, 3);
        this.data.currencyPair.nonBase = this.data.currencyPair.product.substr(3, 3);
        // Check if there is an invert available
        if (this.data.currencyPair.model == null) {
          this.data.currencyPair.model = currencyPairLookup.getCcyPair(
            productCode.substr(3, 3),
            productCode.substr(0, 3),
          );
          this.data.currencyPair.inverted = true;
          this.data.currencyPair.base = this.data.currencyPair.product.substr(3, 3);
          this.data.currencyPair.nonBase = this.data.currencyPair.product.substr(0, 3);
        }

        if (this.data.currencyPair.model) {
          this.data.currencyPair.ndfEnabled = this.isNDFAlgo;
          this.data.currencyPair.fwdEnabled =
            this.isNDFAlgo ||
            (this.data.currencyPair.model.allowAlgoOrders &&
              this.data.currencyPair.model.allowAlgoFwdOrders);
          this.data.currencyPair.spotEnabled =
            !this.isNDFAlgo && this.data.currencyPair.model.allowAlgoOrders;
        } else {
          this.data.currencyPair.fwdEnabled = this.isNDFAlgo;
          this.data.currencyPair.spotEnabled = !this.isNDFAlgo;
        }
        /* RATE SUBSCRIPTION */
        let resubscribe = !(
          typeof this.data.previousValue.product === "string" &&
          this.data.previousValue.product === productCode.substr(3, 3) + productCode.substr(0, 3)
        );

        // subscribe
        if (resubscribe) {
          this.unsubscribeRate(this.rootRefId, this.data.previousValue.product);
          this.rate.value = new CubiqTS.BigNumber(0);
          this.rate.empty = true;
          this.subscribeRate(this.rootRefId, productCode);
        } else if (this.latestRateData) {
          this.onRateUpdate(this.latestRateData, true);
        } else {
          logger.out.error(`no rate data on invert`);
        }

        // update previous product
        this.data.previousValue.product = this.data.currencyPair.product;
        this.data.previousValue.inverted = this.data.currencyPair.inverted;
        //
        if (!this.control.isLoading) {
          this.checkForCalendar();
        }

        //
        this.loadValueDateDataset();

        return true;
      } else {
        this.data.currencyPair.model = null;
        this.data.currencyPair.inverted = false;
        this.data.currencyPair.base = null;
        this.data.currencyPair.nonBase = null;
        this.data.currencyPair.fwdEnabled = this.isNDFAlgo;
        this.data.currencyPair.ndfEnabled = this.isNDFAlgo;
        this.data.currencyPair.spotEnabled = !this.isNDFAlgo;
        /* RATE SUBSCRIPTION */
        // cancel subscription
        this.unsubscribeRate(this.rootRefId, this.data.previousValue.product);
        // reset marketRate
        let displayModel = new OERateDisplayModel();
        displayModel.invalidate();
        this.data.marketRate = displayModel;
        this.icCcyPair.forEach((c) => c.setMarketRate(displayModel));

        this.rate.value = new CubiqTS.BigNumber(0);
        this.rate.empty = true;

        // reset control
        this.outputExtendedLogging(
          `INFO: oe${OENEntryType[this.entryType]}Panel: [${
            this.rootRefId
          }] Resetting requestRate value`,
        );
        // update previous product
        this.data.previousValue.product = null;
        this.data.previousValue.inverted = false;
        //
        if (!this.control.isLoading) {
          this.checkForCalendar();
        }

        //
        this.loadValueDateDataset();

        return true;
      }
    } else {
      return false; // no actual change in the ccyPair
    }
  }

  protected unsubscribeRate(rootRefId: string, productCode: string) {
    this.latestRateData = null;
    this.rate.value = new CubiqTS.BigNumber(0);
    this.rate.empty = true;

    if (this.rateSubscription) {
      this.rateSubscription.unsubscribe();
      this.rateSubscription = null;
    }

    if (typeof productCode === "string" && productCode.length == 6) {
      this.outputExtendedLogging(
        `INFO: oe${OENEntryType[this.entryType]}Panel: [${
          this.rootRefId
        }] requesting Rate UNSUBSCRIBE for ${productCode} ${
          this.rootRefId != rootRefId ? " for (" + rootRefId + ")" : ""
        }`,
      );
      this.api.rateUnsubscribe(rootRefId, this.entryType, productCode);
    }
  }

  protected subscribeRate(rootRefId: string, productCode: string) {
    if (typeof productCode === "string" && productCode.length == 6) {
      this.outputExtendedLogging(
        `INFO: oe${OENEntryType[this.entryType]}Panel: [${
          this.rootRefId
        }] requesting Rate SUBSCRIBE for ${productCode} ${
          this.rootRefId != rootRefId ? " for (" + rootRefId + ")" : ""
        }`,
      );
      let subscription = this.api.rateSubscribe(rootRefId, this.entryType, productCode);
      if (subscription != null) {
        if (this.rateSubscription) {
          this.rateSubscription.unsubscribe();
          this.rateSubscription = null;
        }

        this.rateSubscription = subscription.subscribe(this.onRateUpdate.bind(this));
      } else {
        logger.out.error(
          "ERROR: oe${OENEntryType[this.entryType]}Entry.subscribeRate(): Rate subscription failed!",
        );
      }
    }
  }

  protected onRateUpdate(rate: any, forceRateUpdate: boolean = false): void {
    if (
      rate instanceof OERateTransportModel &&
      rate.refIds instanceof Array &&
      rate.value instanceof OERateDataModel
    ) {
      if (rate.refIds.includes(this.rootRefId)) {
        this.latestRateData = rate; // temp
        let rateData = rate.value;
        if (
          rateData.ccyPair === this.data.currencyPair.product ||
          rateData.invertedCcyPair === this.data.currencyPair.product
        ) {
          let displayModel =
            rateData.invertedCcyPair === this.data.currencyPair.product
              ? rateData.invertedDisplayModel
              : rateData.displayModel;
          let rateValue =
            rateData.invertedCcyPair === this.data.currencyPair.product
              ? rateData.invertedRateValue
              : rateData.rateValue;
          this.outputExtendedLogging(
            `INFO: oe${OENEntryType[this.entryType]}Panel: [${
              this.rootRefId
            }] Received rate ${rateValue.toFixed(4)} for ${this.data.currencyPair.product} (${
              rateData.subscrKey
            })`,
          );

          if (
            displayModel instanceof OERateDisplayModel &&
            rateValue instanceof CubiqTS.BigNumber
          ) {
            // allocate display model
            this.data.marketRate = displayModel;
            this.icCcyPair.forEach((c) => c.setMarketRate(displayModel));

            if (this.rate.empty && !displayModel.empty) {
              this.rate.empty = false;
              this.rate.value = rateValue;
            } else {
              this.rate.value = rateValue;
            }
            this.cd.markForCheck();
          } else {
            logger.out.error(
              `ERROR: oe${
                OENEntryType[this.entryType]
              }Entry.onRateUpdate(): Invalid Rate in Rate update`,
            );
          }
        } else if (this.data.currencyPair.product !== null) {
          logger.out.error(
            `ERROR: oe${
              OENEntryType[this.entryType]
            }Entry.onRateUpdate(): 'Invalid ccyPair in Rate update`,
          );
        }
      } else {
        this.oeLogger.outputRateLogToConsole(
          `INFO: oe${OENEntryType[this.entryType]}Entry.onRateUpdate(): '${
            rate != null && rate.value != null ? rate.value.ccyPair : "Unknown"
          } Rate for` +
            ` RefId ` +
            this.rootRefId +
            " not found in Rate ids [" +
            rate.refIds +
            "] -> Ignore",
        );
      }
    } else {
      logger.out.error(
        `ERROR: oe${
          OENEntryType[this.entryType]
        }Entry.onRateUpdate(): Invalid Rate Transport model`,
      );
    }
  }

  protected loadValueDateDataset(): void {
    // TODO This restriction will change to algo fwd, ndfs need to be removed
    if (
      this.data &&
      this.data.currencyPair &&
      this.data.currencyPair.product &&
      (this.data.currencyPair.fwdEnabled || this.data.currencyPair.spotEnabled)
    ) {
      let base: string = this.data.currencyPair.inverted
        ? this.data.currencyPair.product.substr(3, 3)
        : this.data.currencyPair.product.substr(0, 3);
      let nonBase: string = this.data.currencyPair.inverted
        ? this.data.currencyPair.product.substr(0, 3)
        : this.data.currencyPair.product.substr(3, 3);

      this.calendarService
        .getCalendarWithHolidays(base, nonBase, CubiqTS.Product.FWD)
        .then((calendar: CubiqTS.CurrencyPairCalendarImpl) => {
          if (
            calendar instanceof CubiqTS.CurrencyPairCalendarImpl &&
            Array.isArray(calendar.tenorToValueDateSet) &&
            calendar.tenorToValueDateSet.length > 1 &&
            this.data.currencyPair.spotEnabled &&
            !this.data.currencyPair.fwdEnabled
          ) {
            let reducedCalendar = JSON.parse(JSON.stringify(calendar));

            reducedCalendar.tenorToValueDateMap = new Map();
            reducedCalendar.tenorToValueDateSet = [];
            /* reducedCalendar.tenorToValueAndFixingDateMap = new Map();
					reducedCalendar.tenorToValueAndFixingDateSet = []; */

            let spotTenorToValueDateMapItem = (calendar as any).tenorToValueDateMap.get(
              CubiqTS.Tenor.SPOT,
            );
            if (spotTenorToValueDateMapItem != null) {
              reducedCalendar.tenorToValueDateMap.set(
                CubiqTS.Tenor.SPOT,
                spotTenorToValueDateMapItem,
              );
              reducedCalendar.tenorToValueDateSet.push(spotTenorToValueDateMapItem);
            } else {
              logger.out.error(`DEV-VD: spotTenorToValueDateMapItem not found`);
            }

            /* 	let spotTenorToFixingDateMapItem = (calendar as any).tenorToValueAndFixingDateMap.get(CubiqTS.Tenor.SPOT);
					if(spotTenorToFixingDateMapItem != null){
						reducedCalendar.tenorToValueAndFixingDateMap.set(CubiqTS.Tenor.SPOT, spotTenorToFixingDateMapItem);
						reducedCalendar.tenorToValueAndFixingDateSet.push(spotTenorToFixingDateMapItem)
					} */

            this.data.valueDate.dataset = reducedCalendar;
            this.goodUntilInputControl.forEach((c) =>
              c.refreshGoodUntilConstants(reducedCalendar, base, nonBase),
            );
          } else {
            this.data.valueDate.dataset = calendar;
            this.goodUntilInputControl.forEach((c) =>
              c.refreshGoodUntilConstants(calendar, base, nonBase),
            );
          }
        });
    } else {
      if (this.data.valueDate.dataset != null) {
        this.data.valueDate.dataset = null;
      } else {
        this.onValueDateChange(null);
      }
    }
  }

  protected checkForCalendar(): void {
    if (this.data && this.data.currencyPair && this.data.currencyPair.product) {
      let base: string = this.data.currencyPair.inverted
        ? this.data.currencyPair.product.substr(3, 3)
        : this.data.currencyPair.product.substr(0, 3);
      let nonBase: string = this.data.currencyPair.inverted
        ? this.data.currencyPair.product.substr(0, 3)
        : this.data.currencyPair.product.substr(3, 3);

      this.calendarService
        .getCalendarWithHolidays(base, nonBase, CubiqTS.Product.FWD)
        .then((calendar: CubiqTS.CurrencyPairCalendarImpl) => {
          this.goodUntilInputControl.forEach((c) =>
            c.refreshGoodUntilConstants(calendar, base, nonBase, "DAY", true),
          );
        });
    }
  }

  /**********************************************************************/
  /* MARKUP METHODS */

  public onGetMarkupCriteria($event: any): void {
    this.retrieveMarkup();
  }

  protected updateRetrieveMarkupEnabled(): void {
    if (
      this.data.account.value != null &&
      this.data.client.value != null &&
      this.data.currencyPair.model != null &&
      this.data.fixedCcy.fixedCcy != null &&
      this.data.orderType.value != null &&
      this.data.limitPrice.value != null &&
      this.data.orderAmount.value != null &&
      this.data.algoType.value != null
    ) {
      this.data.markup.retrieveEnabled = true;
    } else {
      this.data.markup.retrieveEnabled = false;
    }
  }

  protected retrieveMarkup() {
    this.data.markup.criteria = null;
    let criteria = this.forwardAdapter.createMarkupCriteria(this.getter.get(this.refId));

    criteria.clientCd = this.data.client.value.clientId;
    criteria.fixedCcy = this.data.fixedCcy.fixedCcy;

    if (this.data.currencyPair.inverted) {
      criteria.nonBase = this.data.currencyPair.model.base;
      criteria.base = this.data.currencyPair.model.nonBase;
    } else {
      criteria.base = this.data.currencyPair.model.base;
      criteria.nonBase = this.data.currencyPair.model.nonBase;
    }
    criteria.clientBuysFixed = this.data.clientIntent.value === CubiqTS.OrderIntent.BUY;
    criteria.requestOrderType = this.data.orderType.value.value;

    // criteria.tradingSource = CubiqTS.TradingSource.INTERNAL; // deprecated
    if (this.data.limitPrice.value !== null) {
      // FXBFXOM-7150
      // criteria.requestRate = this.data.limitPrice.value.textValue; //.toString();
    }
    if (this.data.orderAmount.value !== null) {
      criteria.orderAmount = this.data.orderAmount.value.toString();
    }

    setTimeout(() => {
      this.data.markup.criteria = criteria;
      this.markupInputControl.forEach((c) => c.markupCriteriaUpdate(this.data.markup.criteria));
    }, 0);
  }

  protected updateMarkup(markup: ICMarkupModel) {
    if (this.markupInputControl != null) {
      if (markup == null) {
        this.markupInputControl.forEach((c) => c.writeValue(null));
      } else if (this.data.markup.value !== Number(markup.value)) {
        this.markupInputControl.forEach((c) => c.load(markup));
      }
    } else {
      logger.out.warn(
        `Updating markup when control its not yet rendered, no functional impact ( OMS-36240)`,
      );
    }
  }

  /**********************************************************************/
  /* UTILITY  */

  protected checkMarkupDefault(type: CubiqTS.RequestOrderType): boolean {
    switch (type) {
      case CubiqTS.RequestOrderType.TP:
      case CubiqTS.RequestOrderType.SL_ATRATE:
      case CubiqTS.RequestOrderType.SL_ASK:
      case CubiqTS.RequestOrderType.SL_BID:
      case CubiqTS.RequestOrderType.TSL:
      case CubiqTS.RequestOrderType.TSL_ASK:
      case CubiqTS.RequestOrderType.TSL_BID:
      case CubiqTS.RequestOrderType.MARKET:
        return true;
      default:
        return false;
    }
  }

  /* private isStop(requestOrderType:CubiqTS.RequestOrderType):boolean{
        switch(requestOrderType){
            case CubiqTS.RequestOrderType.SL_ASK:
            case CubiqTS.RequestOrderType.SL_ATRATE:
            case CubiqTS.RequestOrderType.SL_BID:
            case CubiqTS.RequestOrderType.TSL:
            case CubiqTS.RequestOrderType.TSL_ASK:
            case CubiqTS.RequestOrderType.TSL_BID:
            case CubiqTS.RequestOrderType.ALGO:
            case CubiqTS.RequestOrderType.BMALGO:
                return true;
            default:
                return false;
        }
    } */

  /**********************************************************************/
  /* HELPERS - Debug Log - Extended logging  */

  private outputExtendedLogging(value: string, dump: any = null): void {
    this.oeLogger.outputRateLogging(value, dump);
  }

  public updateOBOClient(oboClient: string): void {
    this.api.updateOBOClient(this.refId, oboClient);
  }

  public updateOBOAccount(oboAccount: string): void {
    this.api.updateOBOAccount(this.refId, oboAccount);
  }

  protected isMarkupEnabled(client: CubiqTS.ClientImpl): boolean {
    let applyMarkup: boolean = this.isBranchMarkupEnabled(client);
    return applyMarkup;
  }

  private isBranchMarkupEnabled(client: CubiqTS.ClientImpl): boolean {
    if (client != null) {
      // check if client is sey
      let branchCode = client != null ? client.branchCd : "";
      let branch = this.branchLookupService.getItem(branchCode);
      if (branch instanceof CubiqTS.BranchImpl && branch.applyMarkupFg) {
        // check if client branch is mkt allowed
        return true;
      }
    }
    return false;
  }

  private evaluateMarkupByBranch(clientStr: string): void {
    let client: CubiqTS.ClientImpl = this.clientLookupService.getItem(clientStr);
    let previousMarkupDisabledFg: boolean = this.data.markup.disabled;
    let markupEnabled: boolean = this.isMarkupEnabled(client);

    if (!previousMarkupDisabledFg && !markupEnabled) {
      this.propagateMarkupDefaultData();
      this.api.updateMarkup(this.refId, null);
    }
    this.data.markup.disabled = !markupEnabled;
  }
}
